<template>
  <form @submit.prevent="$emit('submit')">
    <div class="flex items-center justify-between border-b border-gray-200 px-6 py-3">
      <h2 class="text-sm font-semibold text-gray-900">
        Treść slajdu
      </h2>
      <div class="flex items-center gap-2">
        <button
          type="button"
          :class="[
            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            editingLocale === 'pl'
              ? 'bg-indigo-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
          ]"
          @click="editingLocale = 'pl'"
        >
          PL
        </button>
        <button
          type="button"
          :class="[
            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            editingLocale === 'en'
              ? 'bg-indigo-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
          ]"
          @click="editingLocale = 'en'"
        >
          EN
        </button>
      </div>
    </div>

    <div class="-mb-4 -mr-4 flex flex-wrap p-6">
      <text-input
        :model-value="getLocalized('title')"
        :errors="form.errors[`carousel_post.data_${editingLocale}.title`] || form.errors['carousel_post.title']"
        class="w-full pb-4 pr-4"
        :label="`Tytuł (${editingLocale.toUpperCase()})`"
        :required="editingLocale === 'pl'"
        maxlength="255"
        @update:model-value="setLocalized('title', $event)"
      />
      <textarea-input
        :model-value="getLocalized('description')"
        :errors="form.errors[`carousel_post.data_${editingLocale}.description`] || form.errors['carousel_post.description']"
        class="w-full pb-4 pr-4"
        :label="`Opis (${editingLocale.toUpperCase()})`"
        maxlength="1000"
        @update:model-value="setLocalized('description', $event)"
      />
      <text-input
        :model-value="getLocalized('button_text')"
        :errors="form.errors[`carousel_post.data_${editingLocale}.button_text`] || form.errors['carousel_post.button_text']"
        class="w-full pb-4 pr-4 lg:w-1/2"
        :label="`Tekst przycisku (${editingLocale.toUpperCase()})`"
        maxlength="100"
        @update:model-value="setLocalized('button_text', $event)"
      />

      <div class="w-full pb-4 pr-4 lg:w-1/2">
        <resource-link-select
          v-model="form.carousel_post.button_url"
          :link-label="form.carousel_post.button_link_label"
          :type-label="form.carousel_post.button_link_type_label"
          :errors="form.errors['carousel_post.button_url'] || []"
          include-articles
          include-pages
          label="Link przycisku (zasób)"
          placeholder="Wybierz specjalistę, specjalizację, usługę, aktualność lub stronę…"
          @update:link-label="form.carousel_post.button_link_label = $event"
          @update:type-label="form.carousel_post.button_link_type_label = $event"
        />
      </div>

      <div class="w-full pb-4 pr-4 lg:w-1/2">
        <label class="form-label">Witryna</label>
        <site-context-select
          v-model="form.carousel_post.site_context"
          :errors="form.errors['carousel_post.site_context'] || []"
          :options="siteContextOptions"
          class="mt-1 w-full"
          required
        />
      </div>

      <toggle-switch
        v-model="form.carousel_post.show_button"
        :errors="form.errors['carousel_post.show_button'] || []"
        class="w-full pb-4 pr-4"
        label="Wyświetlaj przycisk"
        description="Pokazuje przycisk CTA na slajdzie karuzeli"
      />

      <toggle-switch
        v-model="form.carousel_post.active"
        :errors="form.errors['carousel_post.active'] || []"
        class="w-full pb-4 pr-4 lg:w-1/2"
        label="Aktywny"
        description="Widoczny na stronie publicznej"
      />

      <div class="w-full pb-4 pr-4 lg:w-1/2">
        <file-input
          v-model="form.carousel_post.image"
          :errors="form.errors['carousel_post.image'] || form.errors.image || []"
          label="Obraz"
          accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
        />
        <p class="mt-2 text-xs text-gray-500">
          Ratio 4:3. Przykład: 1200 × 900 px. Obraz zostanie automatycznie przycięty do 4:3.
        </p>
      </div>
      <div v-if="imageUrl" class="w-full pb-4 pr-4 lg:w-1/2">
        <label class="form-label">Aktualny obraz</label>
        <img
          :src="imageUrl"
          alt="Aktualny obraz"
          class="mt-2 h-32 aspect-[4/3] w-auto rounded object-cover"
        >
      </div>
    </div>
    <slot />
  </form>
</template>

<script setup>
import { ref } from 'vue';
import TextInput from '@/Shared/TextInput.vue';
import TextareaInput from '@/Shared/TextareaInput.vue';
import FileInput from '@/Shared/FileInput.vue';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import ResourceLinkSelect from '@/Shared/ResourceLinkSelect.vue';
import SiteContextSelect from '@/Shared/SiteContextSelect.vue';

const props = defineProps({
  form: {
    type: Object,
    required: true,
  },
  imageUrl: {
    type: String,
    default: null,
  },
  siteContextOptions: {
    type: Array,
    default: () => [],
  },
});

defineEmits(['submit']);

const editingLocale = ref('pl');

function localeKey() {
  return `data_${editingLocale.value}`;
}

function ensureLocaleBucket(key) {
  if (!props.form.carousel_post[key]) {
    props.form.carousel_post[key] = {
      title: '',
      description: '',
      button_text: '',
    };
  }
}

function getLocalized(field) {
  const key = localeKey();
  ensureLocaleBucket(key);
  return props.form.carousel_post[key][field] ?? '';
}

function setLocalized(field, value) {
  const key = localeKey();
  ensureLocaleBucket(key);
  props.form.carousel_post[key][field] = value;
}
</script>
